import { useQuery } from "@tanstack/react-query"
import { ArrowLeft, Briefcase, MapPin } from "lucide-react"
import { Link, useParams } from "react-router-dom"
import { api } from "@/api/client"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ApplyForm } from "@/components/careers/apply-form"
import { EmptyState } from "@/components/shared/empty-state"
import { DetailPageSkeleton } from "@/components/shared/loading-skeletons"

type JobResp = {
  organization: { id: string; name: string; slug?: string }
  job: {
    id: string
    title: string
    description: string | null
    requirements: string | null
    location: string | null
    employment_type: string
  }
}

const EMPLOYMENT_LABEL: Record<string, string> = {
  full_time: "Tam zamanlı",
  part_time: "Yarı zamanlı",
  contract: "Sözleşmeli",
  internship: "Stajyer",
}

export function CareersJobPage() {
  const { orgSlug = "", jobSlug = "" } = useParams()
  const q = useQuery({
    queryKey: ["careers-job", orgSlug, jobSlug],
    queryFn: () => api<JobResp>(`/public/careers/${orgSlug}/${jobSlug}`),
  })

  if (q.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <DetailPageSkeleton />
      </div>
    )
  }
  if (q.isError || !q.data) {
    return (
      <div className="grid min-h-screen place-items-center px-6">
        <EmptyState
          icon={Briefcase}
          title="İlan bulunamadı"
          description="Bu pozisyon kapatılmış veya bağlantı geçersiz olabilir."
        />
      </div>
    )
  }

  const { job, organization } = q.data
  return (
    <div className="min-h-screen bg-background">
      <section className="brand-gradient border-b">
        <div className="mx-auto max-w-5xl px-6 py-12">
          <Link
            to={`/careers/${orgSlug}`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" /> {organization.name}
          </Link>
          <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            {job.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {job.location && (
              <Badge variant="secondary" className="gap-1">
                <MapPin className="size-3" /> {job.location}
              </Badge>
            )}
            <Badge variant="outline">
              {EMPLOYMENT_LABEL[job.employment_type] ?? job.employment_type}
            </Badge>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-5xl gap-8 px-6 py-10 lg:grid-cols-[1fr_380px]">
        <div className="space-y-8">
          {job.description && (
            <section>
              <h2 className="mb-3 text-lg font-semibold tracking-tight">İş tanımı</h2>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                {job.description}
              </p>
            </section>
          )}

          {job.requirements && (
            <section>
              <h2 className="mb-3 text-lg font-semibold tracking-tight">Gereksinimler</h2>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                {job.requirements}
              </p>
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-6 lg:self-start">
          <Card className="border-primary/20 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Bu pozisyona başvur</CardTitle>
            </CardHeader>
            <CardContent>
              <ApplyForm jobId={job.id} />
            </CardContent>
          </Card>
        </aside>
      </div>

      <footer className="border-t py-8 text-center text-xs text-muted-foreground">
        Hirefy ile hızlandırılmış işe alım
      </footer>
    </div>
  )
}
